import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import useSound from "use-sound";

import cashIcon from "../../../assets/imgs/exchange/money.png";
import slotMachineBg from "../../../assets/imgs/slot/slot_bg.png";
import slotReelBar from "../../../assets/imgs/slot/slot_reel_bar.png";

import bigWinCoinSfx from "../../../assets/sfxs/Slot/big_win_coins.mp3";
import jackpotWinSfx from "../../../assets/sfxs/Slot/jackpot_win_coins.mp3";
import lowWinSfx from "../../../assets/sfxs/Slot/low_win.mp3";
import lowWinCoinSfx from "../../../assets/sfxs/Slot/low_win_coins.mp3";
import medWinSfx from "../../../assets/sfxs/Slot/medium_win.mp3";
import medWinCoinSfx from "../../../assets/sfxs/Slot/medium_win_coins.mp3";
import spinPullSfx from "../../../assets/sfxs/Slot/pull.mp3";
import spinStopSfx from "../../../assets/sfxs/Slot/spin_3_stops.mp3";

import SlotMachineReel from "./SlotMachineReel";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CustomEventType } from "../../../constants/customEvent";
import {
  SlotMachineBetStatus,
  SlotPayoutType,
  SlotReelSymbol,
} from "../../../constants/enum/slot";
import {
  SlotPayoutAnimationDuration,
  SlotPayouts,
} from "../../../constants/slot";
import useDispatchCustomEvent from "../../../hook/useDispatchCustomEvent";
import useGameBankBalance from "../../../hook/useGameBankBalance";
import {
  clearBetResult,
  updateBetDisabled,
  updateBetStatus,
  updateCurrentSpinIndex,
} from "../../../reducers/slot.slice";
import { SlotBetResult } from "../../../types/SlotBetResult";
import { toUSDFormat } from "../../../utils/utils";
import { generateSpinResult, getPayoutSymbol } from "./helper/payout";
import useIncreasingNumber from "./hook/useIncreasingNumber";

import useStyles from "./index.styles";

const soundEffectParam = {
  loop: false,
  volume: 1,
};

interface ISlotMachineProps {
  account: string;
  betResult?: SlotBetResult;
}

const SlotMachine = ({ account, betResult }: ISlotMachineProps) => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance } = useGameBankBalance(account, signMsg, signature);

  const { currentSpinIndex } = useAppSelector((state) => state.slot);

  const [currentPayoutSymbol, setCurrentPayoutSymbol] = useState(
    SlotPayouts[0]
  );
  const [, setCurrentNonce] = useState(0);
  const [multiplierSum, setMultiplierSum] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [spinResult, setSpinResult] = useState([
    SlotReelSymbol.Health,
    SlotReelSymbol.Health,
    SlotReelSymbol.HelperBot,
  ]);

  const {
    displayNumber,
    startAnimation: startCashIncreasingAnimation,
    resetDisplayNumber,
  } = useIncreasingNumber();

  const [playSpinPull] = useSound(spinPullSfx, soundEffectParam);
  const [playSpinStopSfx] = useSound(spinStopSfx, soundEffectParam);
  const [playLowWinSfx] = useSound(lowWinSfx, soundEffectParam);
  const [playLowCoinSfx] = useSound(lowWinCoinSfx, {
    ...soundEffectParam,
    volume: 0.3,
  });
  const [playMedWinSfx] = useSound(medWinSfx, soundEffectParam);
  const [playMedCoinSfx] = useSound(medWinCoinSfx, {
    ...soundEffectParam,
    volume: 0.3,
  });
  const [playBigWinCoinSfx] = useSound(bigWinCoinSfx, soundEffectParam);
  const [playJackpotWinSfx] = useSound(jackpotWinSfx, soundEffectParam);

  const dispatchCustomEvent = useDispatchCustomEvent();

  const startSpin = useCallback(
    (randomNonce: number) => {
      // const randomNonce = generateRandomNonce();
      const payoutSymbol = getPayoutSymbol(randomNonce);
      const spinResult = generateSpinResult(payoutSymbol);

      setCurrentPayoutSymbol(payoutSymbol);
      setCurrentNonce(randomNonce);
      setSpinResult(spinResult);

      console.log("Starting spin with nonce:", randomNonce);
      console.log("Payout symbol:", payoutSymbol);
      console.log("Spin result:", spinResult);

      playSpinPull();
      playSpinStopSfx();
      setSpinning(true);

      setTimeout(() => {
        setSpinning(false);
      }, 100);
    },
    [playSpinPull, playSpinStopSfx]
  );

  const onCompleteReelAnimation = (reelIndex: number) => {
    // If last reel stopped
    if (reelIndex === spinResult.length - 1) {
      const newMultiplier =
        multiplierSum +
        currentPayoutSymbol.multi * (betResult?.amountPerSpin || 0);
      setMultiplierSum(newMultiplier);

      if (currentPayoutSymbol.payoutType === SlotPayoutType.LowWin) {
        playLowWinSfx();
        playLowCoinSfx();
      } else if (currentPayoutSymbol.payoutType === SlotPayoutType.MedWin) {
        playMedWinSfx();
        playMedCoinSfx();
      } else if (currentPayoutSymbol.payoutType === SlotPayoutType.BigWin) {
        playBigWinCoinSfx();
      } else if (currentPayoutSymbol.payoutType === SlotPayoutType.JackpotWin) {
        playJackpotWinSfx();
      }

      if (currentPayoutSymbol.multi > 0) {
        startCashIncreasingAnimation(
          newMultiplier,
          SlotPayoutAnimationDuration[currentPayoutSymbol.payoutType]
        );

        dispatchCustomEvent(CustomEventType.SlotWin, currentPayoutSymbol);
      }

      if (!betResult) return;
      setTimeout(() => {
        if (betResult.spinCount - 1 > currentSpinIndex) {
          dispatch(updateCurrentSpinIndex(currentSpinIndex + 1));
        } else {
          onCompleteSpin();
        }
      }, SlotPayoutAnimationDuration[currentPayoutSymbol.payoutType] + 500);
    }
  };

  const onCompleteSpin = () => {
    updateBalance(account, signMsg, signature);
    setMultiplierSum(0);

    dispatch(updateCurrentSpinIndex(-1));
    dispatch(updateBetStatus(SlotMachineBetStatus.NOT_REQUESTED));
    dispatch(updateBetDisabled(false));
    dispatch(clearBetResult());
  };

  useEffect(() => {
    if (!betResult) return;

    resetDisplayNumber();
    dispatch(updateCurrentSpinIndex(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betResult, dispatch]);

  useEffect(() => {
    if (currentSpinIndex === -1) return;
    if (!betResult) return;

    startSpin(betResult?.nonce[currentSpinIndex]);
  }, [currentSpinIndex, betResult, startSpin]);

  return (
    <Box className={classes.machine}>
      <Box
        component="img"
        src={slotMachineBg}
        className={classes.machineBg}
      ></Box>

      <Box className={classes.numberReels}>
        <Box
          component="img"
          src={slotReelBar}
          className={classes.reelBar}
        ></Box>

        {spinResult.map((symbol, index) => {
          return (
            <SlotMachineReel
              targetSymbol={symbol}
              key={index}
              spinning={spinning}
              delay={index * 0.2}
              reelIndex={index}
              onCompleteReelAnimation={(reelIndex: number) => {
                onCompleteReelAnimation(reelIndex);
              }}
            />
          );
        })}
      </Box>

      <Box className={classes.balance}>
        <Box
          component="img"
          className={classes.balanceCashIcon}
          src={cashIcon}
        ></Box>
        <Box className={classes.balanceText}>{toUSDFormat(displayNumber)}</Box>
      </Box>

      <Box className={classes.balance}>
        <Box className={classes.balanceText}>
          {currentSpinIndex === -1
            ? betResult
              ? `${betResult.spinCount} / ${betResult.spinCount}`
              : "1 / 1"
            : `${currentSpinIndex + 1} / ${betResult?.spinCount || 1}`}
        </Box>
      </Box>
    </Box>
  );
};

export default SlotMachine;
